/**
 * ============================================================================
 * Front 영역 전용 인증 상태 관리 (Zustand Store)
 * ============================================================================
 * 
 * 주요 타입 요약
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * 1. FrontAuthState   - 인증 상태 (user, token, isAuthenticated 등)
 * 2. FrontAuthActions - 인증 액션 (login, logout, checkAuth 등)
 * 3. FrontAuthStore   - 상태 + 액션 통합 타입
 * 
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * 상태 구조
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * {
 *   user: FrontUser | null,       // 로그인된 사용자 정보
 *   token: string | null,         // JWT 토큰
 *   isAuthenticated: boolean,     // 인증 여부
 *   isLoading: boolean,           // 로딩 상태
 *   preferences: object | null    // 사용자 설정
 * }
 * 
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * 사용 예시
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * import { useFrontAuthStore } from '@/front/auth/store/authStore';
 * 
 * // 컴포넌트에서 사용
 * const { user, isAuthenticated, login, logout } = useFrontAuthStore();
 * 
 * // 역할 확인
 * const isProfessor = user?.role === 'professor';
 * const isStudent = user?.role === 'student';
 * 
 * // 로그인
 * await login(email, password);
 * 
 * // 로그아웃
 * await logout();
 * 
 * // 인증 상태 확인 (앱 초기화 시)
 * await checkAuth();
 * 
 * ============================================================================
 */

import { create } from 'zustand';
import { FrontAuthState, FrontUser } from '../types/auth';
import frontAuthService from '../services/authService';
import { getAuthTokenCookie, deleteAuthTokenCookie } from '../../../core/utils/cookie';

interface FrontAuthActions {
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string, confirmPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  setToken: (token: string | null) => void;
  checkAuth: () => Promise<void>;
  updatePreferences: (preferences: any) => Promise<void>;
}

type FrontAuthStore = FrontAuthState & FrontAuthActions;

export const useFrontAuthStore = create<FrontAuthStore>()(
  (set, get) => ({
    // 초기 상태 (메모리에만 저장, localStorage 사용 안함)
    // 토큰은 쿠키에 저장됨 (authService에서 처리)
    // isLoading: true로 시작하여 checkAuth 완료까지 보호된 라우트에서 대기
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,  // 초기에 true로 설정하여 checkAuth 완료까지 대기
    preferences: null,

      // 액션들
      login: async (email: string, password: string) => {
        // console.log('Front login called');
        set({ isLoading: true });
        try {
          const response = await frontAuthService.login({ email, password });
          
          set({
            user: response.user,
            token: response.token,
            isAuthenticated: true,
            isLoading: false,
            preferences: response.user.preferences,
          });

          frontAuthService.setToken(response.token);
        } catch (error: any) {
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            preferences: null,
          });
          throw error;
        }
      },

      register: async (name: string, email: string, password: string, confirmPassword: string) => {
        // console.log('Front register called');
        set({ isLoading: true });
        try {
          const response = await frontAuthService.register({
            name,
            email,
            password,
            confirmPassword,
            termsAccepted: true,
            privacyAccepted: true,
          });
          
          set({
            user: response.user,
            token: response.token,
            isAuthenticated: true,
            isLoading: false,
            preferences: response.user.preferences,
          });

          frontAuthService.setToken(response.token);
        } catch (error: any) {
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            preferences: null,
          });
          throw error;
        }
      },

      logout: async () => {
        try {
          await frontAuthService.logout();
        } finally {
          // 쿠키에서 토큰 삭제
          deleteAuthTokenCookie();
          // localStorage에서도 삭제 (이전 호환성)
          //localStorage.removeItem('front-auth-storage');

          // 상태 갱신 전에 바로 /로 이동 (ProtectedRoute가 /login으로 리다이렉트하는 것 방지)
          // 전체 페이지 리로드로 checkAuth가 자동으로 상태 초기화
          window.location.href = '/';
        }
      },

      setToken: (token: string | null) => {
        set({ token });
        if (token) {
          frontAuthService.setToken(token);
        }
      },

      checkAuth: async () => {
        // console.log('Front checkAuth called');
        
        // 쿠키에서 토큰 확인
        const cookieToken = getAuthTokenCookie();
        const { token } = get();
        const activeToken = cookieToken || token;
        
        // console.log('Front current token:', activeToken ? '존재함' : '없음');
        
        if (!activeToken) {
          // console.log('No front token, setting unauthenticated');
          set({ isAuthenticated: false, user: null, isLoading: false, token: null });
          return;
        }

        // 쿠키에 토큰이 있으면 상태에도 저장
        if (cookieToken && !token) {
          set({ token: cookieToken });
        }

        // console.log('Front token exists, checking with server');
        set({ isLoading: true });
        try {
          const user = await frontAuthService.getCurrentUser();
          // console.log('Front auth successful, user:', user);
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            preferences: user.preferences,
          });
        } catch (error) {
          // console.log('Front auth failed, clearing token:', error);
          deleteAuthTokenCookie();
          frontAuthService.logout();
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            preferences: null,
          });
        }
      },

    updatePreferences: async (preferences: any) => {
      const { user } = get();
      if (!user) return;

      const updatedUser = {
        ...user,
        preferences: {
          ...user.preferences,
          ...preferences,
        },
      };

      set({
        user: updatedUser,
        preferences: updatedUser.preferences,
      });
    },
  })
);
